/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import VideoStopOverlayIcon from '@atlaskit/icon/core/video-stop-overlay';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import { RovoThinkingIndicator } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingIndicator';

import {
	PageContextType,
	usePageContextType,
} from '../../../../common/ui/chat-input-action-buttons/current-page-as-context';
import type { InlineChatTrace } from '../../../../controllers/inline-chat-controller';

import messages from './messages';

const styles = cssMap({
	escape: {
		paddingInline: token('space.050'),
		paddingBlock: token('space.025'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
		color: token('color.text.subtlest'),
		borderRadius: token('radius.small'),
		font: token('font.body.small'),
	},

	thinkingContainer: {
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},

	thinkingText: {
		color: token('color.text.subtle'),
	},

	actions: {
		marginLeft: 'auto',
	},
});

type ThinkingViewProps = {
	latestTrace: InlineChatTrace | null;
	onCancel: (options?: { skipWarning?: boolean }) => void;
	onFollow?: () => void;
	useShortMessages?: boolean;
};

export const ThinkingView = ({
	latestTrace,
	onCancel,
	onFollow,
	useShortMessages = false,
}: ThinkingViewProps) => {
	di(usePageContextType);
	const { formatMessage } = useIntl();
	const pageContextType = usePageContextType();
	const isNotWhiteboardContext = pageContextType !== PageContextType.CONFLUENCE_WHITEBOARD;

	return (
		<>
			<Box xcss={styles.thinkingContainer}>
				<RovoThinkingIndicator
					title={formatMessage(latestTraceToI18n(latestTrace, useShortMessages))}
				/>
			</Box>
			<Inline
				testId="inline-chat__thinking-view"
				alignBlock="center"
				space="space.100"
				xcss={
					expValEquals('platform_editor_comment_rovoinlinechat_improvement', 'isEnabled', true)
						? styles.actions
						: undefined
				}
			>
				{onFollow && (
					<Button appearance="subtle" onClick={onFollow}>
						{formatMessage(messages.followAiMateButton)}
					</Button>
				)}
				{isNotWhiteboardContext && (
					<Box xcss={styles.escape}>{formatMessage(messages.escapeKeyHint)}</Box>
				)}
				<IconButton
					isTooltipDisabled={false}
					shape="circle"
					onClick={() =>
						onCancel(
							fg('platform_editor_ai_aifc_patch_ga_blockers') ? { skipWarning: true } : undefined,
						)
					}
					icon={(iconProps) => (
						<VideoStopOverlayIcon {...iconProps} color={token('color.icon.danger')} label={''} />
					)}
					label={formatMessage(messages.stopGeneratingButtonLabel)}
				/>
			</Inline>
		</>
	);
};

const latestTraceToI18n = (latestTrace: InlineChatTrace | null, useShortMessages: boolean) => {
	if (
		latestTrace?.message_template === 'ACTION_CLASSIFICATION' &&
		(latestTrace?.action_key === 'aifc-adf-update' ||
			latestTrace?.action_key === 'aifc-confluence-whiteboard-update')
	) {
		return useShortMessages ? messages.editingTextShort : messages.editingText;
	}
	return useShortMessages ? messages.thinkingTextShort : messages.thinkingText;
};
